import {
  ARGUMENT_TYPE_LABELS, BAND_LABELS, CHALLENGE_CATEGORY_LABELS, CLAIM_TYPE_LABELS,
  COMPARISON_DIMENSION_LABELS, WEIGHT_FACTOR_LABELS, WEIGHT_LABELS,
  EPISTEMIC_STATUS_LABELS, EVIDENCE_STRENGTH_LABELS, EVIDENCE_TYPE_LABELS,
  NECESSITY_LABELS, RELATION_KIND_LABELS, SIGNIFICANCE_LABELS, SOURCE_LAYER_LABELS,
} from '@et/schema/i18n';
import type { Locale } from '@et/schema';

/**
 * O namespace `lexicon` e **derivado** das tabelas do schema, nao copiado.
 * Se um enum ganhar um valor sem rotulo, I17 falha no schema e a UI nunca
 * chega a mostrar uma chave crua.
 */
export function lexiconResources(locale: Locale): Record<string, unknown> {
  return {
    status: EPISTEMIC_STATUS_LABELS[locale],
    strength: EVIDENCE_STRENGTH_LABELS[locale],
    significance: {
      ...SIGNIFICANCE_LABELS[locale],
      forClaim: {
        en: '{{level}} for "{{claim}}"',
        es: '{{level}} para "{{claim}}"',
        'pt-PT': '{{level}} para "{{claim}}"',
      }[locale],
    },
    evidenceType: EVIDENCE_TYPE_LABELS[locale],
    claimType: CLAIM_TYPE_LABELS[locale],
    necessity: NECESSITY_LABELS[locale],
    argumentType: ARGUMENT_TYPE_LABELS[locale],
    relationKind: RELATION_KIND_LABELS[locale],
    band: BAND_LABELS[locale],
    dimension: COMPARISON_DIMENSION_LABELS[locale],
    weight: WEIGHT_LABELS[locale],
    weightFactor: WEIGHT_FACTOR_LABELS[locale],
    sourceLayer: SOURCE_LAYER_LABELS[locale],
    challengeCategory: CHALLENGE_CATEGORY_LABELS[locale],
    entityType: {
      en: {
        religion: 'Religion', topic: 'Topic', claim: 'Claim', evidence: 'Evidence',
        source: 'Source', objection: 'Objection', response: 'Response', argument: 'Argument',
        philosophical_position: 'Philosophical position', passage: 'Passage',
        interpretation: 'Interpretation', timeline_event: 'Timeline event', media: 'Media',
        play_sequence: 'Play sequence', step: 'Step', course: 'Course', lesson: 'Lesson',
        comparison_question: 'Comparison', corpus: 'Corpus', corpus_division: 'Book',
      },
      es: {
        religion: 'Religión', topic: 'Tema', claim: 'Afirmación', evidence: 'Evidencia',
        source: 'Fuente', objection: 'Objeción', response: 'Respuesta', argument: 'Argumento',
        philosophical_position: 'Posición filosófica', passage: 'Pasaje',
        interpretation: 'Interpretación', timeline_event: 'Hecho', media: 'Medio',
        play_sequence: 'Secuencia', step: 'Paso', course: 'Curso', lesson: 'Lección',
        comparison_question: 'Comparación', corpus: 'Corpus', corpus_division: 'Libro',
      },
      'pt-PT': {
        religion: 'Religião', topic: 'Tópico', claim: 'Afirmação', evidence: 'Evidência',
        source: 'Fonte', objection: 'Objecção', response: 'Resposta', argument: 'Argumento',
        philosophical_position: 'Posição filosófica', passage: 'Passagem',
        interpretation: 'Interpretação', timeline_event: 'Acontecimento', media: 'Media',
        play_sequence: 'Sequência', step: 'Passo', course: 'Curso', lesson: 'Lição',
        comparison_question: 'Comparação', corpus: 'Corpus', corpus_division: 'Livro',
      },
    }[locale],
    academicLevel: {
      en: { peer_reviewed: 'Peer-reviewed', academic_monograph: 'Academic monograph', critical_edition: 'Critical edition', reference_work: 'Reference work', popular: 'Popular', confessional: 'Confessional', primary_artifact: 'Primary artifact' },
      es: { peer_reviewed: 'Revisado por pares', academic_monograph: 'Monografía académica', critical_edition: 'Edición crítica', reference_work: 'Obra de referencia', popular: 'Divulgación', confessional: 'Confesional', primary_artifact: 'Artefacto primario' },
      'pt-PT': { peer_reviewed: 'Revisto por pares', academic_monograph: 'Monografia académica', critical_edition: 'Edição crítica', reference_work: 'Obra de referência', popular: 'Divulgação', confessional: 'Confessional', primary_artifact: 'Artefacto primário' },
    }[locale],
    perspective: {
      en: { critical: 'Critical', confessional_christian: 'Confessional Christian', confessional_muslim: 'Confessional Muslim', jewish: 'Jewish', secular_academic: 'Secular academic', unspecified: 'Unspecified' },
      es: { critical: 'Crítica', confessional_christian: 'Confesional cristiana', confessional_muslim: 'Confesional musulmana', jewish: 'Judía', secular_academic: 'Académica secular', unspecified: 'Sin especificar' },
      'pt-PT': { critical: 'Crítica', confessional_christian: 'Confessional cristã', confessional_muslim: 'Confessional muçulmana', jewish: 'Judaica', secular_academic: 'Académica secular', unspecified: 'Não especificada' },
    }[locale],
    primaryOrSecondary: {
      en: { primary: 'Primary', secondary: 'Secondary' },
      es: { primary: 'Primaria', secondary: 'Secundaria' },
      'pt-PT': { primary: 'Primária', secondary: 'Secundária' },
    }[locale],
    access: {
      en: { open: 'Open', paywalled: 'Paywalled', physical_only: 'Physical only' },
      es: { open: 'Abierto', paywalled: 'De pago', physical_only: 'Solo físico' },
      'pt-PT': { open: 'Aberto', paywalled: 'Pago', physical_only: 'Só físico' },
    }[locale],
    fallback: {
      en: { explanation: 'Not yet translated — shown in English' },
      es: { explanation: 'Aún sin traducir — se muestra en inglés' },
      'pt-PT': { explanation: 'Ainda por traduzir — apresentado em inglês' },
    }[locale],
  };
}
